import { useEffect } from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import HomePage from "@/components/pages/HomePage";
import WebsitesPage from "@/components/pages/WebsitesPage";
import RevenueSystemsPage from "@/components/pages/RevenueSystemsPage";
import PackagesPage from "@/components/pages/PackagesPage";
import PricingPage from "@/components/pages/PricingPage";
import FaqPage from "@/components/pages/FaqPage";
import IntakePage from "@/components/pages/IntakePage";
import RevenueLeakAuditPage from "@/components/pages/RevenueLeakAuditPage";
import BookPage from "@/components/pages/BookPage";
import InsightsPage from "@/components/pages/InsightsPage";
import InsightArticlePage from "@/components/pages/InsightArticlePage";
import PrivacyPage from "@/components/pages/PrivacyPage";
import TermsPage from "@/components/pages/TermsPage";
import ThankYouPage from "@/components/pages/ThankYouPage";
import PaymentSuccessPage from "@/components/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/components/pages/PaymentCancelPage";
import AdminIntakesPage from "@/components/pages/AdminIntakesPage";
import AdminPaymentsPage from "@/components/pages/AdminPaymentsPage";
import AdminIntakeDetailPage from "@/components/pages/AdminIntakeDetailPage";
import AdminDeliveriesPage from "@/components/pages/AdminDeliveriesPage";import AdminDeliveryWorkspacePage from "@/components/pages/AdminDeliveryWorkspacePage";
import ScrollToTop from "@/components/site/ScrollToTop";
import GhlChatWidget from "@/components/site/GhlChatWidget";
import { Toaster } from "@/components/ui/sonner";
import { initAttribution } from "@/lib/attribution";

/* Map any legacy `open-audit` event detail or old URL `?offer=…` value
 * onto a service slug consumed by /intake?service=…. Anything we don't
 * recognise routes to the Revenue Leak Audit (entry-point service). */
const OFFER_TO_SERVICE = {
  "revenue audit": "revenue-leak-audit",
  "revenue leak audit": "revenue-leak-audit",
  audit: "revenue-leak-audit",
  "lead capture system": "lead-capture",
  lead_capture: "lead-capture",
  "follow-up + booking system": "follow-up",
  "follow up + booking system": "follow-up",
  follow_up: "follow-up",
  "revenue conversion system": "revenue-conversion",
  revenue_conversion: "revenue-conversion",
  "website build": "website-build",
  "starter website": "website-build",
  "growth website": "website-build",
  "premium website": "website-build",
  starter: "website-build",
  growth: "website-build",
  premium: "website-build",
  consultation: "consultation",
};

function offerToServiceSlug(offer) {
  if (!offer) return "revenue-leak-audit";
  const key = String(offer).trim().toLowerCase();
  return OFFER_TO_SERVICE[key] || "revenue-leak-audit";
}

/** Listens for legacy `open-audit` CTA dispatches across the site (Nav, Hero,
 *  Pricing, Insights, etc.) and routes the user to the canonical /intake
 *  flow with the right service pre-selected. */
function OpenAuditRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    const handler = (e) => {
      const slug = offerToServiceSlug(e?.detail?.offer);
      navigate(`/intake?service=${slug}`);
    };
    window.addEventListener("open-audit", handler);
    return () => window.removeEventListener("open-audit", handler);
  }, [navigate]);
  return null;
}

/** Legacy redirect: every `?package=…` on the old /revenue-brief or
 *  /build-brief routes maps to the canonical /intake?service=… so old
 *  bookmarks/links never 404. */
function LegacyIntakeRedirect({ defaultService }) {
  const [params] = useSearchParams();
  const raw = params.get("package") || params.get("pkg") || params.get("service");
  const slug = raw ? offerToServiceSlug(raw) : defaultService;
  return <Navigate to={`/intake?service=${slug}`} replace />;
}

function App() {
  useEffect(() => {
    initAttribution();
  }, []);

  return (
    <div className="App font-sans">
      <HelmetProvider>
        <BrowserRouter>
          <ScrollToTop />
          <OpenAuditRedirect />
          <GhlChatWidget />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/websites" element={<WebsitesPage />} />
            <Route path="/revenue-systems" element={<RevenueSystemsPage />} />
            <Route path="/packages" element={<PackagesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/intake" element={<IntakePage />} />
            <Route path="/revenue-leak-audit" element={<RevenueLeakAuditPage />} />
            <Route path="/book" element={<BookPage />} />
            {/* Legacy intakes — redirect to the canonical /intake form. */}
            <Route
              path="/build-brief"
              element={<LegacyIntakeRedirect defaultService="website-build" />}
            />
            <Route
              path="/revenue-brief"
              element={<LegacyIntakeRedirect defaultService="revenue-leak-audit" />}
            />
            <Route path="/insights" element={<InsightsPage />} />
            <Route path="/insights/:slug" element={<InsightArticlePage />} />
            <Route path="/thank-you" element={<ThankYouPage />} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/payment/cancel" element={<PaymentCancelPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/admin/intakes" element={<AdminIntakesPage />} />
            <Route path="/admin/payments" element={<AdminPaymentsPage />} />
            <Route path="/admin/intakes/:briefId" element={<AdminIntakeDetailPage />} />
            <Route path="/admin/deliveries" element={<AdminDeliveriesPage />} />
            <Route path="/admin/deliveries/:workspaceId" element={<AdminDeliveryWorkspacePage />} />
          </Routes>
        </BrowserRouter>
      </HelmetProvider>
      <Toaster
        richColors
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#FFFCF7",
            color: "#171717",
            border: "1px solid #D8CFC2",
          },
        }}
      />
    </div>
  );
}

export default App;
